import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import { stripesConnect, withStripes } from '@folio/stripes-core';
import { translations } from 'stripes-config';
import AppsTranslationsContext from './AppsTranslationsContext';

function AppsTranslationsProvider(props) {
  const [localTranslations, setLocalTranslations] = useState({});
  const [staticTranslations, setStaticTranslations] = useState({});

  // ── Build local translations map from translation_keys records ──────────
  const buildLocalTranslations = () => {
    const records = (props.resources.translationKeys || {}).records || [];
    const appsTrans = {};

    records.forEach((tk) => {
      if (!appsTrans[tk.localeValue]) {
        appsTrans[tk.localeValue] = { messages: {}, metadata: null };
      }
      appsTrans[tk.localeValue].messages[tk.translationKey] = tk.value || '';
      // keep the most recent metadata (last record wins)
      if (tk.metadata) {
        appsTrans[tk.localeValue].metadata = tk.metadata;
      }
    });

    return appsTrans;
  };

  // ── Load static translations from stripes-config bundle files ───────────
  useEffect(() => {
    const doFetch = (locale) => {
      const parentLocale = locale.split('-')[0];
      const region = locale.replace('-', '_');
      return fetch(
        translations[region] ? translations[region] : translations[parentLocale],
      )
        .then((res) => res.json())
        .then((trans) => ({ [locale]: trans }));
    };

    const promises = Object.keys(translations).map(doFetch);

    Promise.all(promises)
      .then((prom) => setStaticTranslations(Object.assign({}, ...prom)))
      .catch((e) => console.error('Failed to load static translations', e)); // eslint-disable-line no-console
  }, []);

  // ── Sync local translations when translation-keys records change ─────────
  useEffect(() => {
    const built = buildLocalTranslations();
    if (!isEqual(built, localTranslations)) {
      setLocalTranslations(built);
    }
  }, [props.resources.translationKeys]);

  return (
    <AppsTranslationsContext.Provider
      value={{
        local: localTranslations,   // from translation_keys (server)
        stripes: staticTranslations, // from static bundle files (client)
      }}
    >
      {props.children}
    </AppsTranslationsContext.Provider>
  );
}

AppsTranslationsProvider.propTypes = {
  children: PropTypes.node,
  resources: PropTypes.shape({
    translationKeys: PropTypes.shape({
      records: PropTypes.arrayOf(PropTypes.object),
    }),
  }),
};

AppsTranslationsProvider.manifest = {
  translationKeys: {
    type: 'okapi',
    path: 'translation-keys?query=categoryName%3D%3DstripesApps&limit=2147483647',
    records: 'translationKeys',
    resourceShouldRefresh: true,
  },
};

export default withStripes(stripesConnect(AppsTranslationsProvider));
